import { Injectable, OnInit } from '@angular/core';
import { AtsLoggerService } from '@ats/logger';
import { atsSortFn, Unsubscribable } from '@ats/utility';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CmTableColumn, CmTableConfig, CmTableDatarow, CmTableGroup, CmTableRow, CmTableSort } from '../models';

const enum TableActionTypes {
	SET_COLUMNS = 'Add the columns',
	SET_GRID_STYLES = 'Set the grid styles',
	SET_GROUPS = 'Add the groups',
	SET_ROWS = 'Add the table rows',
	SET_SELECTABLE = 'Set table selectability',
	UPDATE_IS_SELECTING = 'Update if the table is selecting',
	UPDATE_VISIBLE_ROWS = 'Update the visible rows',
	UPDATE_FILTER = 'Update the current filter text',
	UPDATE_SORT = 'Update the current sort order',
	TOGGLE_LOADING = 'Toggle loading indicator',
}

interface TableAction {
	readonly type: TableActionTypes;
	readonly payload:
		| CmTableGroup[]
		| CmTableDatarow[]
		| CmTableRow[]
		| CmTableColumn[]
		| string
		| string[]
		| CmTableSort[]
		| boolean;
}

class TableState {
	groups: CmTableGroup[] = [];
	columns: CmTableColumn[] = [];
	rows: CmTableDatarow[] = null;
	visible: CmTableRow[] = [];
	sort: string[] = [];
	filter = '';
	loading = true;
	gridStyles: string;
	selectable = false;
	isSelecting = null;
}

const reduce = (state: TableState, action: TableAction) => {
	switch (action.type) {
		case TableActionTypes.SET_COLUMNS:
			return {
				...state,
				columns: action.payload as CmTableColumn[],
			};
		case TableActionTypes.SET_GROUPS:
			return {
				...state,
				groups: action.payload as CmTableGroup[],
			};
		case TableActionTypes.SET_ROWS:
			return {
				...state,
				rows: action.payload as CmTableDatarow[],
			};
		case TableActionTypes.UPDATE_VISIBLE_ROWS:
			return {
				...state,
				visible: action.payload as CmTableRow[],
			};
		case TableActionTypes.UPDATE_FILTER:
			return {
				...state,
				filter: action.payload as string,
			};
		case TableActionTypes.UPDATE_SORT:
			return {
				...state,
				sort: action.payload as string[],
			};
		case TableActionTypes.TOGGLE_LOADING:
			return {
				...state,
				loading: action.payload as boolean,
			};
		case TableActionTypes.SET_GRID_STYLES:
			return {
				...state,
				gridStyles: action.payload as string,
			};
		case TableActionTypes.SET_SELECTABLE:
			return {
				...state,
				selectable: action.payload as boolean,
			};
		case TableActionTypes.UPDATE_IS_SELECTING:
			return {
				...state,
				isSelecting: action.payload as boolean,
			};
	}
};

@Injectable()
export class CmTableService extends Unsubscribable implements OnInit {
	private state = new TableState();
	private dispatch = new BehaviorSubject<TableState>(this.state);
	private loaders$ = new BehaviorSubject<string[]>([]);
	private selecting$ = new BehaviorSubject<boolean>(this.state.isSelecting);
	private sourceData: CmTableDatarow[] = [];

	columns$ = this.dispatch.asObservable().pipe(map(state => state.columns));

	groups$ = this.dispatch.asObservable().pipe(map(state => state.groups));

	rows$ = this.dispatch.asObservable().pipe(
		filter(state => !state.loading && state.rows !== null),
		map(state => state.visible)
	);

	selection$ = this.dispatch.asObservable().pipe(
		filter(state => !state.loading && state.rows !== null && state.isSelecting),
		map(state => ({
			selected: state.rows.filter(row => row.selected && !!row.data),
			remaining: state.rows.filter(row => !row.selected && !!row.data),
		}))
	);

	filter$ = this.dispatch.asObservable().pipe(map(state => state.filter));

	sort$ = this.dispatch.asObservable().pipe(map(state => state.sort));

	isSelecting$ = this.dispatch.asObservable().pipe(map(state => state.isSelecting));

	gridStyles$ = this.dispatch.asObservable().pipe(map(state => state.gridStyles));

	isLoading$: Observable<boolean> = this.loaders$.asObservable().pipe(map(loaders => loaders.length > 0));

	hasSummaryImpact = ['toggle-all-groups', 'toggle-group', 'sort'];

	constructor(private logger: AtsLoggerService) {
		super();
	}

	ngOnInit() {
		this.init();
	}

	config(config$: Observable<CmTableConfig>, filter$: Observable<string>) {
		config$
			.pipe(
				takeUntil(this.unsub),
				switchMap(config =>
					filter$.pipe(
						take(1),
						map(filter => ({ ...config, filter }))
					)
				)
			)
			.subscribe(({ rows, groups, columns, secured, isSelecting$, filter }) => {
				this.toggleLoading('config');

				groups = groups.map(group => ({
					...group,
					id: group.id && !this.isEndDelimited(group.id) ? group.id + '_' : group.id,
					parentGroup:
						group.parentGroup && !this.isEndDelimited(group.parentGroup)
							? group.parentGroup + '_'
							: group.parentGroup,
				}));

				rows = rows.map(row => ({
					...row,
					parentGroup:
						row.parentGroup && !this.isEndDelimited(row.parentGroup)
							? row.parentGroup + '_'
							: row.parentGroup,
				}));

				this.sourceData = rows.map(row => ({ ...row }));

				columns = columns.map(col => {
					let filtered = true;
					if (!this.state.columns.length) {
						filtered = Object.prototype.hasOwnProperty.call(col, 'filtered') ? col.filtered : true;
					} else {
						const current = this.state.columns.find(column => column.id === col.id);
						filtered =
							current && Object.prototype.hasOwnProperty.call(current, 'filtered')
								? current.filtered
								: filtered;
					}

					return {
						...col,
						filtered,
					};
				});

				if (secured)
					columns = columns.map(col => ({
						...col,
						hidden: !secured.find(item => item.column === col.id)
							? col.hidden
							: !secured.find(item => item.column === col.id).passed,
					}));

				if (isSelecting$) {
					this.setSelectable(true);
					isSelecting$.pipe(takeUntil(this.unsub)).subscribe(isSelecting => this.setIsSelecting(isSelecting));
				}

				this.setGroups(groups);
				this.setRows(rows);
				this.setColumns(columns);
				this.setFilter(filter, false);
				this.updateRowVisibility('config');
			});

		// Skip the first filter value since config is getting it
		filter$
			.pipe(
				skip(1),
				distinctUntilChanged(),
				tap(() => this.toggleLoading('filter')),
				takeUntil(this.unsub)
			)
			.subscribe(dataFilter => this.setFilter(dataFilter));

		this.isLoading$.pipe(takeUntil(this.unsub)).subscribe(isLoading => this.setLoading(isLoading));
	}

	toggleSelecting(cancel = false) {
		if (!this.state.selectable) return;

		if (this.state.isSelecting && cancel) {
			this.setRows(this.sourceData.map(row => ({ ...row })));
			if (this.state.sort.length > 0) this.sortColumns();
			else this.updateRowVisibility();
		}

		this.setIsSelecting(!this.state.isSelecting);
	}

	toggleGroup(id: string) {
		this.toggleLoading('toggle-group');
		if (!this.state.visible.find(item => item.id === id && item.childCount > 0)) return;
		const groups = this.state.groups.slice();
		const group = groups.find(gr => gr.id === id);
		if (group) {
			group.open = !group.open;
			if (!group.open)
				groups.forEach(grp => {
					grp.open = grp.parentGroup && grp.parentGroup.includes(group.id) ? false : grp.open;
				});
			this.setGroups(groups);
			this.updateRowVisibility('toggle-group');
		}
	}

	toggleSelection(row: CmTableRow | CmTableRow[]) {
		this.toggleLoading('selection');
		let rows = this.state.rows.slice();

		if (Array.isArray(row)) {
			row.forEach(rw => (rows = this.mapRowSelection(rw, rows)));
		} else {
			rows = this.mapRowSelection(row, rows);
		}

		this.setRows(rows);
		this.updateRowVisibility('selection');

		return this.loaders$.asObservable().pipe(
			filter(loaders => !loaders.includes('selection')),
			take(1),
			mapTo(true)
		);
	}

	clearSelection() {
		this.toggleLoading('selection');
		const rows = this.state.rows.slice().map(row => ({
			...row,
			selected: row.disabled ? row.selected : false,
		}));

		this.setRows(rows);
		this.updateRowVisibility('selection');

		return this.loaders$.asObservable().pipe(
			filter(loaders => !loaders.includes('selection')),
			take(1),
			mapTo(true)
		);
	}

	private mapRowSelection(row: CmTableRow, rows: CmTableDatarow[]) {
		const group = this.state.groups.find(grp => grp.id === row.id && !!row.data);
		const parent = this.state.rows.find(rw => !!rw.data && rw.id === row.parentGroup);
		rows.forEach(rw => {
			if (rw.data && rw.disabled) return;
			if (
				// The row we are toggling
				(row.data && rw.id === row.id) ||
				// Toggled a group not a row
				(!row.data && !rw.filtered && rw.parentGroup && rw.parentGroup.includes(row.id)) ||
				// Turned a row that has children off - Turn off the children as well
				(group && row.selected && rw.data && rw.parentGroup.includes(group.id)) ||
				// Turned on a child whose parent is off - Turn on the parent
				(parent && !parent.selected && rw.data && rw.id === parent.id)
			) {
				rw.selected = !row.selected;
			}
			// It is a parent row and has a uneditable child feature on, it has to stay on
			if (
				group &&
				rw.data &&
				rw.id === group.id &&
				this.state.rows
					.filter(child => !!child.data && child.parentGroup.includes(group.id))
					.some(child => child.disabled && child.selected)
			)
				rw.selected = true;
		});

		return rows;
	}

	toggleAllGroups(level?: number) {
		this.toggleLoading('toggle-all-groups');
		// setTimeout is used to unblock the call stack and show the loading overlay
		setTimeout(() => {
			const allOpen = this.state.groups.filter(group => !level || group.level > level).every(group => group.open);
			this.setGroups(
				this.state.groups.map(group => ({
					...group,
					open: !level || group.level > level ? !allOpen : true,
				}))
			);
			this.updateRowVisibility('toggle-all-groups');
		}, 10);
	}

	toggleSort(id: string) {
		this.toggleLoading('sort');
		// setTimeout is used to unblock the call stack and show the loading overlay
		setTimeout(() => {
			let found = false;
			const sort = this.state.sort.map(col => {
				if (id === col) {
					col = '-' + col;
					found = true;
				} else if ('-' + id === col) {
					col = '';
					found = true;
				}
				return col;
			});
			if (!found) sort.push(id);
			this.setSort(sort.filter(col => col !== ''));
			this.sortColumns();
		}, 10);
	}

	toggleFilter(id: string) {
		this.toggleLoading('filter');
		const columns = this.state.columns.slice();
		const column = columns.find(col => col.id === id);
		if (column) column.filtered = !column.filtered;
		this.setColumns(columns);
		this.updateRowVisibility('filter');
	}

	toggleAllFilters() {
		this.toggleLoading('filter');
		const columns = this.state.columns.slice();
		const anyNotFiltered = columns.some(col => !col.filtered);
		columns.forEach(col => (col.filtered = anyNotFiltered));
		this.setColumns(columns);
		this.updateRowVisibility('filter');
	}

	private isEndDelimited(str: string, delimiter: string = '_') {
		return str.slice(-1) === delimiter;
	}

	private init() {
		// Watch for loading changes
		this.loaders$
			.asObservable()
			.pipe(
				map(loaders => loaders.length > 0),
				distinctUntilChanged(),
				takeUntil(this.unsub)
			)
			.subscribe(loading => {
				const type = TableActionTypes.TOGGLE_LOADING;
				const action = { payload: loading, type };
				this.dispatch.next((this.state = reduce(this.state, action)));
			});
	}

	private sortColumns() {
		const sort = [...this.state.sort];
		const source = this.sourceData.map(row => ({ ...row }));
		// Save off the groups for later
		const groups = this.state.rows.filter(row => !row.data).map(row => ({ ...row }));
		if (sort.length > 0) {
			// Get an array of just the row data
			const rowData = this.state.rows.filter(row => !!row.data).map(row => ({ rowId: row.id, ...row.data }));
			// Sort the data
			const newOrder = atsSortFn.apply(this, [rowData, ...sort]).map(row => row.rowId);
			// Map the data back to the original rows and include the groups
			this.setRows([
				...groups,
				...newOrder.map(rowId => source.find(row => row.id.toString() === rowId.toString())),
			]);
		} else {
			this.setRows(source);
		}
		this.updateRowVisibility('sort');
	}

	private updateRowVisibility(loader?: string) {
		this.applyFilter();
		const rows = this.state.rows.map(row => ({ ...row }));

		const hasSummary = this.state.columns.some(col => !!col.summary);
		let summaryData = {};
		if (hasSummary) {
			const existingSummary = this.state.visible.find(row => row.id === 'summary');
			if (this.hasSummaryImpact.includes(loader) && existingSummary) {
				summaryData = existingSummary.data;
			} else {
				this.state.columns.forEach(col => {
					summaryData[col.id] = col.summary
						? rows
								.filter(row => !row.filtered)
								.map(row => row.data[col.id])
								.reduce(col.summary.reducer, col.summary.init)
						: null;
				});
			}
		}
		if (!this.state.groups.length) {
			if (hasSummary)
				rows.unshift({
					id: 'summary',
					data: summaryData,
					filtered: false,
				});
			this.setVisibleRows(rows as CmTableRow[]);
			if (loader) this.toggleLoading(loader);
			return;
		}
		const groupSortKeys =
			this.state.groups && this.state.groups.every(grp => Object.prototype.hasOwnProperty.call(grp, 'order'))
				? ['order', 'id']
				: ['id'];
		const groups = atsSortFn([...this.state.groups.map(group => ({ ...group }))], ...groupSortKeys);
		const openGroups = groups.filter(group => group.open).map(group => group.id);
		const firstRow = [];
		if (hasSummary)
			firstRow.push({
				id: 'summary',
				data: summaryData,
				filtered: false,
			});
		if (rows.length > 0) {
			this.setVisibleRows([
				...firstRow,
				...groups
					.filter(group => !group.parentGroup || openGroups.includes(group.parentGroup))
					.flatMap(group => {
						const classes = ['cm-table__group--level-' + group.level.toString()];
						const childCount = rows.filter(row => !row.filtered && row.parentGroup.startsWith(group.id))
							.length;
						const selected =
							rows.some(row => row.selected) &&
							rows
								.filter(
									row =>
										!row.filtered &&
										(row.parentGroup.startsWith(group.id) ||
											row.parentGroup.includes('_' + group.id))
								)
								.every(row => row.selected);
						if (childCount === 0) classes.push('cm-table__group--disabled');
						const newData = [
							{
								...group,
								childCount,
								classes,
								selected,
							},
							...rows
								.filter(
									row =>
										openGroups.includes(row.parentGroup) &&
										row.parentGroup === group.id &&
										!row.filtered
								)
								.flat(),
						];
						return newData as CmTableRow[];
					}),
			]);
		}
		if (loader) this.toggleLoading(loader);
	}

	private applyFilter() {
		const rows = this.state.rows.slice();
		const filteredColumns = this.state.columns
			.filter(col => col.filtered && !col.hidden)
			.map(col => (col.filterKey ? col.filterKey : col.id));

		rows.forEach(
			row =>
				(row.filtered =
					filteredColumns.length > 0
						? !filteredColumns.some(column =>
								row.data[column]
									? row.data[column]
											.toString()
											.toLowerCase()
											.includes(this.state.filter.toLowerCase())
									: false
						  )
						: false)
		);

		this.setRows(rows.slice());
	}

	/******* STATE MANAGERS **********/
	private setRows(rows: CmTableDatarow[]) {
		const type = TableActionTypes.SET_ROWS;
		const action = { payload: rows.slice(), type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}
	private setVisibleRows(rows: CmTableRow[]) {
		const type = TableActionTypes.UPDATE_VISIBLE_ROWS;
		const action = { payload: rows.slice(), type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setFilter(dataFilter: string, update: boolean = true) {
		const type = TableActionTypes.UPDATE_FILTER;
		const action = { payload: dataFilter, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
		if (update)
			setTimeout(() => {
				this.updateRowVisibility('filter');
			}, 10);
	}

	private setGroups(groups: CmTableGroup[]) {
		const type = TableActionTypes.SET_GROUPS;
		const action = { payload: groups.slice(), type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setSort(sort: string[]) {
		const type = TableActionTypes.UPDATE_SORT;
		const action = { payload: sort.slice(), type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setIsSelecting(isSelecting: boolean) {
		const type = TableActionTypes.UPDATE_IS_SELECTING;
		const action = { payload: isSelecting, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setSelectable(selectable: boolean) {
		const type = TableActionTypes.SET_SELECTABLE;
		const action = { payload: selectable, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setColumns(columns: CmTableColumn[]) {
		const type = TableActionTypes.SET_COLUMNS;
		const action = { payload: columns.slice(), type };
		this.dispatch.next((this.state = reduce(this.state, action)));
		if (!this.state.gridStyles) this.setGridStyles();
	}

	private setLoading(isLoading: boolean) {
		const type = TableActionTypes.TOGGLE_LOADING;
		const action = { payload: isLoading, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private setGridStyles() {
		const gridStyles = this.state.columns
			.filter(col => !col.hidden)
			.slice()
			.map(col =>
				col.sizing
					? col.sizing
					: col.relativeWidth
					? 'minmax(0, ' + col.relativeWidth.toString() + 'fr)'
					: 'minmax(0, 1fr)'
			)
			.join(' ');
		// if (this.state.visible && this.state.visible.some(row => row.selectable))
		// 	gridStyles = '30px ' + gridStyles;
		const type = TableActionTypes.SET_GRID_STYLES;
		const action = {
			payload: gridStyles,
			type,
		};
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private toggleLoading(id: string) {
		const loaders = this.loaders$.getValue().slice();
		if (loaders.includes(id)) this.loaders$.next(loaders.filter(loader => loader !== id));
		else this.loaders$.next([id, ...loaders]);
	}
	/******* END STATE MANAGERS **********/
}
